import { DMS, type GQL } from "@binale-tech/shared";
import { captureException } from "@sentry/react";
import { saveAs } from "file-saver";
import { ref as refStorage, uploadBytesResumable } from "firebase/storage";
import JSZip from "jszip";
import { getDocument } from "@domain/pdf/pdfjs";
import { getSharedPdfWorker } from "@domain/pdf/sharedPdfWorker";
import { storage } from "@domain/api/firebase/firebase";
import {
    documentCreate,
    documentsDelete,
    documentsTypeUpdate,
    documentsUpdate,
} from "@domain/context/mutations/documentMutations.graphql";
import { logger } from "@domain/infrastructure/logger";
import { type DmsType } from "@domain/models/dms";
import { DmsDefaultSubType, type TUploadFile, UploadFileStatus } from "@dms/types";
import { convertDocumentToUpdateInput } from "@dms/scripts/helpers/convertDocumentToUpdateInput";
import { type ApolloClient } from "@apollo/client";
import { fetchDmsDocumentUrl } from "@domain/api/dmsDocumentUrl";

export type TFileUploadEnrichedData = {
    fileData: Pick<TUploadFile, "file" | "hash" | "isUploaded">;
    documentType: { type: DmsType; subType?: string };
    companyData: GQL.ICompany;
};

type DocumentsApiCreateInput = {
    fileUploadData: TFileUploadEnrichedData;
    client: ApolloClient;
    setFileStatus: (hash: string, data: Pick<TUploadFile, "status" | "error" | "isUploaded">) => void;
};

export class DocumentsApi {
    private static readonly maxFileSize = DMS.MAX_UPLOAD_BYTES;
    private static readonly NS = "files";
    private static readonly fileFolderName = "pdf";

    static createDocument = async (arg: DocumentsApiCreateInput) => {
        const { fileUploadData, client, setFileStatus } = arg;
        const { fileData, documentType } = fileUploadData;

        await this.uploadFile({
            fileUploadData,
            client,
            setFileStatus,
        }).catch((err) => {
            setFileStatus(fileData.hash, {
                error: err.message,
                status: UploadFileStatus.ERROR,
                isUploaded: false,
            });
            return Promise.reject(err);
        });

        const input: GQL.IDocumentCreateInput = {
            key: fileUploadData.fileData.hash,
            fileName: fileUploadData.fileData.file.name,
            companyId: fileUploadData.companyData.id,

            type: documentType.type,
            subType: documentType.subType,
        };

        await client.mutate({
            mutation: documentCreate,
            variables: { input },
        });

        setFileStatus(fileData.hash, {
            error: false,
            status: UploadFileStatus.DONE,
            isUploaded: true,
        });

        return { fileRes: { ...fileData, isUploaded: true } };
    };

    static uploadFile = async (arg: DocumentsApiCreateInput): Promise<void> => {
        const { fileUploadData, setFileStatus } = arg;
        return new Promise((resolve, reject) => {
            const { file, hash } = fileUploadData.fileData;
            const { companyData } = fileUploadData;

            if (file.size > this.maxFileSize) {
                setFileStatus(hash, {
                    error: `Size limit ${this.maxFileSize} bytes`,
                    status: UploadFileStatus.ERROR,
                    isUploaded: false,
                });

                reject(new Error(`Size limit ${this.maxFileSize} bytes`));
                return;
            }

            const name = hash + ".pdf";
            const { id: companyId } = companyData;

            const fileRef = refStorage(storage, [this.NS, companyId, this.fileFolderName, name].join("/"));
            const uploadTask = uploadBytesResumable(fileRef, file, { contentType: "application/pdf" });

            setFileStatus(hash, {
                error: false,
                status: UploadFileStatus.PROGRESS,
                isUploaded: false,
            });

            uploadTask.on(
                "state_changed",
                () => {},
                (error: { message: string }) => {
                    setFileStatus(hash, {
                        error: error.message,
                        status: UploadFileStatus.ERROR,
                        isUploaded: false,
                    });

                    reject(error);
                },
                () => {
                    resolve();
                },
            );
        });
    };

    static updateDocuments = async (arg: {
        documents: GQL.IDocument[];
        client: ApolloClient;
    }): Promise<{ documentsUpdate: boolean }> => {
        const { documents, client } = arg;
        const documentInputs = documents.map(convertDocumentToUpdateInput);

        const result = await client.mutate({
            mutation: documentsUpdate,
            variables: { input: documentInputs },
        });

        return { documentsUpdate: result.data?.documentsUpdate };
    };

    static changeDocumentsType = async (documents: GQL.IDocument[], client: ApolloClient) => {
        const checkSubtype = (subType: string) => {
            if (!subType) {
                return false;
            }

            const disallowedSubTypes: string[] = [DmsDefaultSubType.no_subTypes, DmsDefaultSubType.all_subTypes];

            const hasSubstring =
                subType.includes(DmsDefaultSubType.no_subTypes) || subType.includes(DmsDefaultSubType.all_subTypes);

            return !disallowedSubTypes.includes(subType) && !hasSubstring;
        };

        const documentTypeInputs = documents.map((doc) => {
            const subType = checkSubtype(doc.subType) ? doc.subType : null;
            return {
                id: doc.key,
                type: doc.type,
                subType,
                companyId: doc.companyId,
            };
        });

        await client.mutate({
            mutation: documentsTypeUpdate,
            variables: { input: documentTypeInputs },
        });
    };

    /**
     * Calls the cascade-aware `documentsDelete` mutation. Backend decides per
     * doc: hard delete / move to trash / keep (attached + already in trash).
     * Returns the buckets so callers can surface a meaningful toast.
     */
    static async removeDocuments(documents: GQL.IDocument[], client: ApolloClient): Promise<GQL.IDocumentsDelete> {
        const { data } = await client.mutate({
            mutation: documentsDelete,
            variables: { companyId: documents[0].companyId, ids: documents.map((doc) => doc.key) },
        });
        return (
            data?.documentsDelete ?? {
                hardDeleted: [],
                movedToTrash: [],
                keptAttached: [],
            }
        );
    }

    // `downloadFile` and `bulkDownloadFiles` both mint a fresh signed URL
    // per file through the `dmsDocumentUrl` resolver - that resolver also
    // writes one `dmsDocumentAccessLog` row per call, so a bulk download of
    // N documents produces N audit rows (the desired behaviour: each
    // contained file is a distinct user-intent access).
    static downloadFile = async (document: GQL.IDocument, client: ApolloClient, fileName?: string) => {
        try {
            const minted = await fetchDmsDocumentUrl(client, document.key, document.companyId);
            if (!minted) {
                throw new Error("No URL returned for document " + document.key);
            }
            const response = await fetch(minted.url);
            const blob = await response.blob();
            saveAs(blob, fileName);
        } catch (error) {
            captureException(error);
        }
    };

    // Returns when the archive is saved (or after logging a failure). The caller owns any
    // loading flag via its own try/finally - this stays a React-free domain API.
    static bulkDownloadFiles = async (files: GQL.IDocument[], client: ApolloClient) => {
        const zip = new JSZip();
        const usedFileNames = new Set<string>();

        try {
            for (const [index, file] of files.entries()) {
                const name = usedFileNames.has(file.fileName) ? `copy-${index}_${file.fileName}` : file.fileName;
                const minted = await fetchDmsDocumentUrl(client, file.key, file.companyId);
                if (!minted) {
                    // The resolver returned no URL for this doc (permission
                    // edge case, race with deletion, transient resolver
                    // error). The zip continues with remaining files -
                    // partial export is better than aborting - but the gap
                    // must reach Sentry so we can investigate. Silent
                    // drops produce silently-incomplete DATEV/Belege
                    // exports downstream.
                    captureException(new Error("bulkDownloadFiles: no URL returned for document"), {
                        extra: { documentId: file.key, companyId: file.companyId, fileName: file.fileName },
                    });
                    continue;
                }
                const response = await fetch(minted.url);
                const blob = await response.blob();
                zip.file(name, blob, { binary: true });
                usedFileNames.add(name);
            }
            const content = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
            saveAs(content, "dms-documents");
        } catch (error) {
            logger.crit(error);
            captureException(error);
        }
    };

    static urlFormatter = (url: string = ""): string => {
        const indexQueryStart = url.indexOf("?");
        return url.slice(0, indexQueryStart);
    };

    static checkPDF = (file: File): Promise<{ isValid: boolean; error?: Error }> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    const data = reader.result as ArrayBuffer;
                    const loadingTask = getDocument({
                        data,
                        worker: getSharedPdfWorker(),
                        useSystemFonts: true,
                        wasmUrl: "/wasm/",
                        cMapUrl: "/cmaps/",
                    });
                    const pdf = await loadingTask.promise;
                    const numPages = pdf.numPages;
                    await pdf.cleanup();
                    resolve({ isValid: Boolean(numPages) });
                } catch (error) {
                    resolve({ isValid: false, error });
                }
            };
            reader.readAsArrayBuffer(file);
        });
    };
}
